import { useState, useEffect, useCallback } from "react";
import axios from "axios";

// 타입 정의
export interface ChatMessage {
  messageId: number;
  messageType: "USER" | "BOT";
  messageContent: string;
  regDttm: string;
  userRating?: number;
  feedbackText?: string;
}

export interface ChatSession {
  sessionId: number;
  sessionUuid: string;
  userName: string;
  sessionStatus: string;
  totalMessages: number;
  startedAt: string;
  lastActivityAt: string;
}

export interface UserInfo {
  userName: string;
  userPhone: string;
  userEmail: string;
  userCompany?: string;
}

export interface ChatResponse {
  success: boolean;
  message: string;
  sessionUuid?: string;
  messageId?: number;
  data?: any;
  error?: string;
}

// 로컬스토리지 키
const STORAGE_KEYS = {
  SESSION_UUID: "superQnaChat_sessionUuid",
  USER_INFO: "superQnaChat_userInfo",
  MESSAGES: "superQnaChat_messages",
} as const;

/**
 * Super QnA Chat Hook
 * @description QnA 채팅 기능을 위한 커스텀 훅
 */
export const useSuperQnaChat = () => {
  // 상태 관리
  const [sessionUuid, setSessionUuid] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSessionActive, setIsSessionActive] = useState(false);

  // API 기본 URL (환경에 따라 조정)
  const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3050";

  // 로컬스토리지에서 데이터 복원
  useEffect(() => {
    const savedSessionUuid = localStorage.getItem(STORAGE_KEYS.SESSION_UUID);
    const savedUserInfo = localStorage.getItem(STORAGE_KEYS.USER_INFO);
    const savedMessages = localStorage.getItem(STORAGE_KEYS.MESSAGES);

    if (savedSessionUuid) {
      setSessionUuid(savedSessionUuid);
      setIsSessionActive(true);
    }

    if (savedUserInfo) {
      try {
        setUserInfo(JSON.parse(savedUserInfo));
      } catch (e) {
        console.warn("Failed to parse saved user info:", e);
      }
    }

    if (savedMessages) {
      try {
        setMessages(JSON.parse(savedMessages));
      } catch (e) {
        console.warn("Failed to parse saved messages:", e);
      }
    }
  }, []);

  // 메시지 변경 시 로컬스토리지에 저장
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem(STORAGE_KEYS.MESSAGES, JSON.stringify(messages));
    }
  }, [messages]);

  // 세션 시작
  const startChat = useCallback(
    async (userData: UserInfo): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await axios.post<ChatResponse>(
          `${API_BASE_URL}/api/v1/customer/super-qna-chat/start`,
          {
            userName: userData.userName,
            userPhone: userData.userPhone,
            userEmail: userData.userEmail,
            userCompany: userData.userCompany,
            sourceChannel: "WEBSITE",
            referrerUrl: window.location.href,
          }
        );

        if (response.data.success && response.data.sessionUuid) {
          const newSessionUuid = response.data.sessionUuid;

          // 상태 업데이트
          setSessionUuid(newSessionUuid);
          setUserInfo(userData);
          setIsSessionActive(true);

          // 로컬스토리지에 저장
          localStorage.setItem(STORAGE_KEYS.SESSION_UUID, newSessionUuid);
          localStorage.setItem(
            STORAGE_KEYS.USER_INFO,
            JSON.stringify(userData)
          );

          // 환영 메시지 추가
          if (response.data.messageId) {
            const welcomeMessage: ChatMessage = {
              messageId: response.data.messageId,
              messageType: "BOT",
              messageContent: response.data.message,
              regDttm: new Date().toISOString(),
            };
            setMessages([welcomeMessage]);
          }

          return true;
        } else {
          setError(response.data.error || "채팅 시작에 실패했습니다.");
          return false;
        }
      } catch (err: any) {
        console.error("Failed to start chat:", err);
        setError(
          err.response?.data?.message || "네트워크 오류가 발생했습니다."
        );
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [API_BASE_URL]
  );

  // 메시지 전송
  const sendMessage = useCallback(
    async (message: string): Promise<boolean> => {
      if (!sessionUuid || !message.trim()) {
        return false;
      }

      setIsLoading(true);
      setError(null);

      // 사용자 메시지를 즉시 UI에 추가
      const userMessage: ChatMessage = {
        messageId: Date.now(), // 임시 ID
        messageType: "USER",
        messageContent: message.trim(),
        regDttm: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, userMessage]);

      try {
        const response = await axios.post<ChatResponse>(
          `${API_BASE_URL}/api/v1/customer/super-qna-chat/message`,
          {
            sessionUuid,
            message: message.trim(),
          }
        );

        if (response.data.success && response.data.messageId) {
          // 봇 응답 메시지 추가
          const botMessage: ChatMessage = {
            messageId: response.data.messageId,
            messageType: "BOT",
            messageContent: response.data.message,
            regDttm: new Date().toISOString(),
          };

          setMessages((prev) => [...prev, botMessage]);
          return true;
        } else {
          setError(response.data.error || "메시지 전송에 실패했습니다.");
          return false;
        }
      } catch (err: any) {
        console.error("Failed to send message:", err);
        setError(
          err.response?.data?.message || "네트워크 오류가 발생했습니다."
        );
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [sessionUuid, API_BASE_URL]
  );

  // 채팅 초기화
  const resetChat = useCallback(async (): Promise<boolean> => {
    if (!sessionUuid) {
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post<ChatResponse>(
        `${API_BASE_URL}/api/v1/customer/super-qna-chat/reset`,
        {
          sessionUuid,
        }
      );

      if (response.data.success) {
        // 메시지 초기화
        const welcomeMessage: ChatMessage = {
          messageId: response.data.messageId || Date.now(),
          messageType: "BOT",
          messageContent: response.data.message,
          regDttm: new Date().toISOString(),
        };

        setMessages([welcomeMessage]);
        return true;
      } else {
        setError(response.data.error || "채팅 초기화에 실패했습니다.");
        return false;
      }
    } catch (err: any) {
      console.error("Failed to reset chat:", err);
      setError(err.response?.data?.message || "네트워크 오류가 발생했습니다.");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [sessionUuid, API_BASE_URL]);

  // 채팅 종료
  const endChat = useCallback(() => {
    // 로컬스토리지 클리어
    localStorage.removeItem(STORAGE_KEYS.SESSION_UUID);
    localStorage.removeItem(STORAGE_KEYS.USER_INFO);
    localStorage.removeItem(STORAGE_KEYS.MESSAGES);

    // 상태 초기화
    setSessionUuid(null);
    setUserInfo(null);
    setMessages([]);
    setIsSessionActive(false);
    setError(null);
  }, []);

  // 기존 세션 복원
  const restoreSession = useCallback(async (): Promise<boolean> => {
    if (!sessionUuid) {
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      // 세션 정보 조회
      const sessionResponse = await axios.get<ChatSession>(
        `${API_BASE_URL}/api/v1/customer/super-qna-chat/session/${sessionUuid}`
      );

      // 메시지 목록 조회
      const messagesResponse = await axios.get<ChatMessage[]>(
        `${API_BASE_URL}/api/v1/customer/super-qna-chat/messages/${sessionUuid}`
      );

      setMessages(messagesResponse.data);
      setIsSessionActive(true);
      return true;
    } catch (err: any) {
      console.error("Failed to restore session:", err);
      // 세션 복원 실패 시 로컬 데이터 사용
      setError("세션 복원에 실패했지만 로컬 데이터를 사용합니다.");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [sessionUuid, API_BASE_URL]);

  return {
    // 상태
    sessionUuid,
    userInfo,
    messages,
    isLoading,
    error,
    isSessionActive,

    // 액션
    startChat,
    sendMessage,
    resetChat,
    endChat,
    restoreSession,

    // 유틸리티
    clearError: () => setError(null),
  };
};
